<template>
  <div class="card">
    <div class="card__info">
      <img :src="getIcon(0, token.tokenContractAddress)" alt="token-icon" class="card__icon" />

      <div class="card__details">
        <div class="card__name">{{ token.name }}</div>
        <div class="card__balance">{{ formatBalance(token.balance) }}</div>

        <div class="card__address-wrapper">
          <span class="card__address">{{ formatAddress(token.tokenContractAddress) }}</span>
          <template v-if="isSupported">
            <IconsCopy
              v-if="!copied"
              class="tw:w-5 tw:h-5 tw:cursor-pointer"
              @click="handleCopyAddress(token?.tokenContractAddress)"
            />
            <IconsCheckMark v-else class="tw:w-5 tw:h-5 tw:text-white" />
          </template>
          <a :href="blockScanUrl" target="_blank">
            <LinkNewTab class="card__link-icon" />
          </a>
        </div>
      </div>
    </div>

    <div class="card__action">
      <HalloweenGenericButton class="card__button" :disabled="isProcessing" @click="handleClick">
        <span v-if="isMobile">+</span>
        <span v-else>{{ $t('walletTokenAddressModalAddToken') }}</span>
        <template #icon>
          <img src="/img/icons/mm-icon.png" alt="metamask" />
        </template>
      </HalloweenGenericButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import LinkNewTab from '~/components/icons/LinkNewTab.vue';
// import Copy from '~/components/icons/Copy.vue';
import type { ITokenModel } from '~/types/apiService';
import { Formatters } from '~/utils/formatters/formatters';

const { blockchain } = useEnvs();
const { isMobile } = useDevice();
const { copy, isSupported, copied } = useClipboard();

const props = defineProps<{
  token: ITokenModel & { balance: string };
  isProcessing: boolean;
}>();

const emit = defineEmits<{
  (e: 'add', token: ITokenModel & { balance: string }): void;
}>();

const blockScanUrl = computed(() => `${blockchain.blockScanUrl}/address/${props.token.tokenContractAddress}`);

const handleCopyAddress = (tokenAddress: string) => copy(tokenAddress);

const handleClick = () => {
  emit('add', props.token);
};

const formatBalance = (value: string | number) => Formatters.number.renderString((f) => f.formatNumber(value));
</script>

<style lang="scss" scoped>
.card {
  min-height: 96px;
  max-height: 112px;
  min-width: 424px;
  height: 100%;
  width: 100%;

  border: 2px solid #133442;
  background-color: #081b25;
  border-radius: 24px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  gap: 40px;

  @media screen and (max-width: 768px) {
    min-width: 328px;
    gap: 16px;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__icon {
    width: 56px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__name {
    font-family: Grenze;
    font-weight: 600;
    font-size: 20px;
    line-height: 100%;
    color: #ffffff;
  }

  &__balance {
    font-family: Grenze;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    color: #8e979c;
  }

  &__address-wrapper {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &__address {
    font-family: Grenze;
    font-weight: 600;
    font-size: 16px;
    line-height: 100%;
    color: #ffffff;
  }

  &__copy-icon,
  &__link-icon {
    width: 20px !important;
  }

  &__action {
    img {
      width: 20px;
    }
  }

  &__button {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    flex-direction: row-reverse !important;

    @media screen and (max-width: 768px) {
      padding: 10px !important;
      gap: 4px !important;
    }
  }
}
</style>
